import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { LicenseExpiryBadge } from '@/components/technicians/LicenseExpiryBadge';
import { useTechnicianProfilesByUserId } from '@/routes/technicians/useTechnicianLicenses';

interface UserSummary {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  status: string;
}

/**
 * The account roster. Deliberately plain — this exists so M0-22 has a real
 * permission-gated screen to enforce on; the designed version arrives with the
 * app shell (M0-24) and invites (M2-20).
 */
export function People() {
  const { t } = useTranslation();
  const hasPermission = useHasPermission();
  const canManage = hasPermission(Permissions.manageUsers);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => apiGet<UserSummary[]>('/api/v1/users'),
    retry: false,
  });

  // M1-37: the AC's "badge on the admin list". ONE extra request for the whole
  // roster, not one per technician — the profile list carries each profile's
  // licence counts, and this indexes them by user id so a row can find its own.
  // Same `users.read` gate as the roster itself, so anyone who can see this
  // screen can see the badges too.
  const licenseSummaries = useTechnicianProfilesByUserId();

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('nav.people')}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {canManage ? t('people.subtitleCanManage') : t('people.subtitleReadOnly')}
          </p>
        </div>
        {/* users.manage, so a Dispatcher who can read the roster does not get
            a link to a screen the API would refuse them (M1-38). */}
        {canManage ? (
          <div className="flex flex-col items-start gap-1 sm:items-end">
            {/* M2-20: inviting, roles and deactivation live under Settings;
                this roster stays the read-only view a Dispatcher also has. */}
            <Link to="/settings/users" className="text-sm underline underline-offset-2">
              {t('people.manageUsers')}
            </Link>
            <Link to="/people/skills" className="text-sm underline underline-offset-2">
              {t('people.manageSkills')}
            </Link>
          </div>
        ) : null}
      </div>

      {users.isPending ? (
        <LoadingState label={t('people.loading')} />
      ) : users.isError ? (
        <ErrorState
          title={t('people.loadErrorTitle')}
          description={t('people.loadErrorDescription')}
          onRetry={() => void users.refetch()}
        />
      ) : users.data.length === 0 ? (
        <EmptyState
          title={t('people.emptyTitle')}
          description={
            canManage ? t('people.emptyDescriptionCanManage') : t('people.emptyDescriptionReadOnly')
          }
        />
      ) : (
        <div
          role="region"
          aria-label={t('people.tableRegion')}
          className="overflow-x-auto rounded-lg border"
          // WCAG 2.1.1 (M1-42), and of the four tables this is the one that
          // measurably fails. Only Email is hidden on a narrow screen; Role, Status,
          // Licences and the actions column stay at every width, so the row cannot
          // shrink to fit. Measured in Chrome: 70px off the right edge at a 768px
          // viewport, and 356px at the 320px reflow width WCAG 1.4.10 asks for —
          // more than half the roster unreachable.
          //
          // ⚠️ The last column holds a "View profile" link, so tabbing to it does
          // scroll this, which is why the gap was easy to miss. But that link renders
          // only for a Technician, so an office-only roster has nothing focusable
          // past the first cell and no keyboard route to the overflow at all.
          // Reachability that depends on the DATA is not reachability.
          //
          // Named, because a focusable region a screen reader cannot announce is
          // half a fix. `<div role="region">` rather than a `<section>` for the
          // lint reason recorded in eslint.config.js; the computed role is the
          // same. ⚠️ Unlike the other three tables this one carries no
          // <caption>, so this label is the only thing naming it — which is a
          // reason to keep it accurate, not a reason to make it do a caption's
          // job of describing the columns.
          tabIndex={0}
        >
          <table className="w-full text-sm">
            <thead className="border-b bg-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-2 text-left font-medium">{t('people.columnName')}</th>
                <th className="hidden px-4 py-2 text-left font-medium sm:table-cell">
                  {t('people.columnEmail')}
                </th>
                <th className="px-4 py-2 text-left font-medium">{t('people.columnRole')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('people.columnStatus')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('people.columnLicenses')}</th>
                <th className="px-4 py-2 text-left font-medium">
                  <span className="sr-only">{t('people.columnActions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((user) => (
                <tr key={user.id} className="border-b last:border-b-0">
                  <td className="px-4 py-2">
                    <span className="block">{user.name}</span>
                    {/* The email column is hidden on a phone, so keep it reachable. */}
                    <span className="block text-xs text-[var(--color-muted-foreground)] sm:hidden">
                      {user.email}
                    </span>
                  </td>
                  <td className="hidden px-4 py-2 text-[var(--color-muted-foreground)] sm:table-cell">
                    {user.email}
                  </td>
                  <td className="px-4 py-2">{user.role}</td>
                  <td className="px-4 py-2">{user.status}</td>
                  <td className="px-4 py-2">
                    <LicenseExpiryBadge summary={licenseSummaries.data?.get(user.id)} />
                  </td>
                  <td className="px-4 py-2">
                    {/* M1-24: only Technicians have a profile today — the
                        entity is scoped to that role even though nothing at
                        the database level enforces it (Domain/TechnicianProfile.cs). */}
                    {user.role === 'Technician' ? (
                      <Link
                        to={`/technicians/${user.id}`}
                        className="text-[var(--color-muted-foreground)] underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                      >
                        {t('people.viewProfile')}
                      </Link>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
